import { useState, useEffect, useCallback } from "react";
import {
  Coffee,
  Mail,
  Lock,
  Key,
  AlertCircle,
  Globe,
  Send,
  Shield,
  UserPlus,
  LogIn,
} from "lucide-react";
import cloudflareApi from "../lib/cloudflare-api";
import { getAuthState, getCloudflareConfig } from "../lib/storage";
import { Button, Input, Card } from "./ui";

interface AuthScreenProps {
  onAuthSuccess: () => void;
}

type AuthMode = "login" | "register" | "migrate";

export default function AuthScreen({ onAuthSuccess }: AuthScreenProps) {
  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [workerUrl, setWorkerUrl] = useState(
    "https://mocaccino-mail-router.mocaccinomail.workers.dev"
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [checkingAuth, setCheckingAuth] = useState(true);

  const [mailgunApiKey, setMailgunApiKey] = useState("");
  const [mailgunDomain, setMailgunDomain] = useState("");
  const [mailgunFromEmail, setMailgunFromEmail] = useState("");

  useEffect(() => {
    checkAuthStateOnLoad();
  }, []);

  const checkAuthStateOnLoad = () => {
    try {
      setCheckingAuth(true);
      const state = getAuthState();

      if (state.logged_in) {
        onAuthSuccess();
        return;
      }

      if (state.needs_migration && state.email) {
        setEmail(state.email);
        setMode("migrate");
      }
    } catch (err) {
      console.error("Error checking auth state:", err);
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleLogin = async () => {
    if (!email) {
      setError("Por favor ingresa tu email");
      return;
    }

    if (!password) {
      setError("Por favor ingresa tu contrasena");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const config = getCloudflareConfig();
      if (config.worker_url) {
        cloudflareApi.setWorkerUrl(config.worker_url);
      } else {
        cloudflareApi.setWorkerUrl(workerUrl);
      }

      await cloudflareApi.login(email, password);
      onAuthSuccess();
    } catch (err: any) {
      const errorMsg = err.message || err.toString();
      if (
        errorMsg === "NEEDS_MIGRATION" ||
        errorMsg.includes("NEEDS_MIGRATION")
      ) {
        setMode("migrate");
        setError("");
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!email || !password || !confirmPassword) {
      setError("Por favor completa todos los campos obligatorios");
      return;
    }

    if (password !== confirmPassword) {
      setError("Las contrasenas no coinciden");
      return;
    }

    if (password.length < 8) {
      setError("La contrasena debe tener al menos 8 caracteres");
      return;
    }

    if (mailgunApiKey || mailgunDomain || mailgunFromEmail) {
      if (!mailgunApiKey || !mailgunDomain || !mailgunFromEmail) {
        setError(
          "Si configuras Mailgun, debes completar API Key, Dominio y Email remitente"
        );
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      const mailgunConfig = mailgunApiKey
        ? {
            api_key: mailgunApiKey,
            domain: mailgunDomain,
            from_email: mailgunFromEmail,
            from_name: "Mocaccino",
          }
        : undefined;

      await cloudflareApi.register(email, password, workerUrl, mailgunConfig);
      onAuthSuccess();
    } catch (err: any) {
      const errorMsg = err.message || err.toString();
      if (errorMsg === "NEEDS_MIGRATION") {
        setMode("migrate");
        setError(
          "Ya existe una cuenta con este email. Necesitas migrar tu cuenta."
        );
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMigrate = async () => {
    if (!email || !password || !apiKey || !confirmPassword) {
      setError("Por favor completa todos los campos");
      return;
    }

    if (password !== confirmPassword) {
      setError("Las contrasenas no coinciden");
      return;
    }

    if (password.length < 8) {
      setError("La contrasena debe tener al menos 8 caracteres");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const config = getCloudflareConfig();
      if (config.worker_url) {
        cloudflareApi.setWorkerUrl(config.worker_url);
      } else {
        cloudflareApi.setWorkerUrl(workerUrl);
      }

      await cloudflareApi.migrate(email, apiKey, password);
      onAuthSuccess();
    } catch (err: any) {
      setError(err.message || err.toString());
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (mode === "login") handleLogin();
      else if (mode === "register") handleRegister();
      else handleMigrate();
    },
    [mode, email, password, confirmPassword, apiKey, workerUrl, mailgunApiKey, mailgunDomain, mailgunFromEmail]
  );

  const switchMode = useCallback(
    (newMode: AuthMode) => {
      setMode(newMode);
      setError("");
    },
    []
  );

  const modeConfig = {
    login: {
      title: "Bienvenido de vuelta",
      subtitle: "Inicia sesion para continuar con Mocaccino",
      submitLabel: "Iniciar sesion",
      submitIcon: <LogIn size={18} />,
    },
    register: {
      title: "Crea tu cuenta",
      subtitle: "Unete al correo mas fresco del mercado",
      submitLabel: "Crear cuenta",
      submitIcon: <UserPlus size={18} />,
    },
    migrate: {
      title: "Actualizacion de seguridad",
      subtitle: "Configura tu nueva contrasena segura",
      submitLabel: "Actualizar cuenta",
      submitIcon: <Shield size={18} />,
    },
  };

  const currentMode = modeConfig[mode];

  // Loading state - clean and fast
  if (checkingAuth) {
    return (
      <div className="auth-screen relative">
        <div className="flex flex-col items-center gap-4" style={{ opacity: 0, animation: 'authFadeIn 200ms ease forwards' }}>
          <Coffee size={36} className="text-lime-500" strokeWidth={1.5} />
          <p className="text-sm text-[var(--text-secondary)] font-medium">
            Verificando sesion...
          </p>
        </div>
        <style>{`
          @keyframes authFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '12px 0',
    background: 'none',
    border: 'none',
    borderBottom: active ? '2px solid var(--primary, #84cc16)' : '2px solid transparent',
    marginBottom: '-1px',
    color: active ? 'var(--primary, #84cc16)' : 'var(--text-tertiary)',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'color 150ms ease, border-color 150ms ease',
  });

  return (
    <div className="auth-screen relative" style={{ background: 'var(--bg-primary)' }}>
      {/* Subtle static gradient background */}
      <div
        className="pointer-events-none fixed inset-0"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(132,204,22,0.06) 0%, rgba(234,179,8,0.03) 40%, transparent 70%)',
        }}
      />

      {/* Main card */}
      <div
        className="relative z-10 w-full max-w-[420px] mx-4"
        style={{ opacity: 0, animation: 'authFadeIn 200ms ease forwards' }}
      >
        <Card
          variant="glass"
          padding="none"
          className="overflow-hidden shadow-citric-neon"
        >
          {/* Top accent bar */}
          <div
            className="h-1 w-full"
            style={{
              background:
                "linear-gradient(90deg, #84cc16 0%, #65a30d 100%)",
            }}
          />

          <div className="p-8 sm:p-10">
            {/* Branding */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center mb-4">
                <div
                  className="relative w-[56px] h-[56px] rounded-2xl flex items-center justify-center"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(132,204,22,0.12) 0%, rgba(234,179,8,0.08) 100%)",
                    border: "1.5px solid rgba(132,204,22,0.2)",
                  }}
                >
                  <Coffee
                    size={28}
                    className="text-lime-500 dark:text-lime-400"
                    strokeWidth={1.8}
                  />
                </div>
              </div>

              <h1 className="text-3xl font-bold tracking-tight mb-1">
                <span className="text-gradient-citric">Mocaccino</span>
              </h1>

              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                {currentMode.subtitle}
              </p>
            </div>

            {/* Tabs - Login / Register (not shown in migrate mode) */}
            {mode !== "migrate" && (
              <div style={{
                display: 'flex',
                borderBottom: '1px solid var(--border)',
                marginBottom: '24px',
              }}>
                <button
                  type="button"
                  onClick={() => switchMode('login')}
                  style={tabStyle(mode === 'login')}
                >
                  Iniciar sesion
                </button>
                <button
                  type="button"
                  onClick={() => switchMode('register')}
                  style={tabStyle(mode === 'register')}
                >
                  Registrarse
                </button>
              </div>
            )}

            {/* Migration info box */}
            {mode === "migrate" && (
              <div
                className="flex gap-3 p-4 rounded-xl mb-6"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(132,204,22,0.08), rgba(234,179,8,0.06))",
                  border: "1px solid rgba(132,204,22,0.2)",
                }}
              >
                <Shield
                  size={20}
                  className="text-lime-500 shrink-0 mt-0.5"
                />
                <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
                  Hemos mejorado la seguridad de Mocaccino. Por favor, crea
                  una contrasena para tu cuenta. Tu configuracion se
                  mantendra.
                </p>
              </div>
            )}

            {/* Error display */}
            {error && (
              <div
                className="flex items-start gap-3 p-4 rounded-xl mb-6 text-sm"
                style={{
                  background: "rgba(239,68,68,0.06)",
                  border: "1px solid rgba(239,68,68,0.15)",
                  color: "#ef4444",
                  animation: 'authFadeIn 150ms ease forwards',
                }}
              >
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span className="leading-relaxed">{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <Input
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  disabled={mode === "migrate"}
                  autoFocus={mode !== "migrate"}
                  icon={<Mail size={18} />}
                />
              </div>

              {/* API Key (migrate only) */}
              {mode === "migrate" && (
                <div>
                  <Input
                    type="password"
                    label="API Key actual"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="Tu API key de 64 caracteres"
                    hint="La API key que usabas antes para autenticarte"
                    icon={<Key size={18} />}
                    showPasswordToggle
                  />
                </div>
              )}

              {/* Password */}
              <div>
                <Input
                  type="password"
                  label={
                    mode === "migrate" ? "Nueva contrasena" : "Contrasena"
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimo 8 caracteres"
                  autoFocus={mode === "migrate"}
                  icon={<Lock size={18} />}
                  showPasswordToggle
                />
              </div>

              {/* Confirm password (register / migrate) */}
              {(mode === "register" || mode === "migrate") && (
                <div>
                  <Input
                    type="password"
                    label="Confirmar contrasena"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repite la contrasena"
                    icon={<Lock size={18} />}
                    showPasswordToggle
                  />
                </div>
              )}

              {/* Register-only fields */}
              {mode === "register" && (
                <>
                  {/* Worker URL */}
                  <div>
                    <Input
                      type="url"
                      label="URL del Worker"
                      value={workerUrl}
                      onChange={(e) => setWorkerUrl(e.target.value)}
                      placeholder="https://tu-worker.workers.dev"
                      hint="La URL de tu Cloudflare Worker"
                      icon={<Globe size={18} />}
                    />
                  </div>

                  {/* Mailgun divider */}
                  <div className="relative flex items-center gap-4 py-2">
                    <div className="flex-1 h-px bg-[var(--border)]" />
                    <span className="text-xs font-medium text-[var(--text-tertiary)] whitespace-nowrap flex items-center gap-1.5">
                      <Send size={12} />
                      Configuracion de Mailgun (opcional)
                    </span>
                    <div className="flex-1 h-px bg-[var(--border)]" />
                  </div>

                  {/* Mailgun API Key */}
                  <div>
                    <Input
                      type="password"
                      label="API Key de Mailgun"
                      value={mailgunApiKey}
                      onChange={(e) => setMailgunApiKey(e.target.value)}
                      placeholder="key-xxxxxxxxxxxxxxxx"
                      hint="Tu Private API Key de Mailgun"
                      icon={<Key size={18} />}
                      showPasswordToggle
                    />
                  </div>

                  {/* Mailgun Domain */}
                  <div>
                    <Input
                      type="text"
                      label="Dominio de Mailgun"
                      value={mailgunDomain}
                      onChange={(e) => setMailgunDomain(e.target.value)}
                      placeholder="tudominio.com"
                      hint="Tu dominio verificado en Mailgun"
                      icon={<Globe size={18} />}
                    />
                  </div>

                  {/* Mailgun From Email */}
                  <div>
                    <Input
                      type="email"
                      label="Email remitente"
                      value={mailgunFromEmail}
                      onChange={(e) => setMailgunFromEmail(e.target.value)}
                      placeholder="noreply@tudominio.com"
                      hint="Direccion desde la que enviaras correos"
                      icon={<Send size={18} />}
                    />
                  </div>
                </>
              )}

              {/* Submit */}
              <div className="pt-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={loading}
                  disabled={loading}
                  icon={!loading ? currentMode.submitIcon : undefined}
                  iconPosition="right"
                  className="w-full text-base font-semibold"
                >
                  {loading ? "Procesando..." : currentMode.submitLabel}
                </Button>
              </div>
            </form>
          </div>
        </Card>

        {/* Bottom tagline */}
        <p
          className="text-center text-xs text-[var(--text-tertiary)] mt-6 opacity-60"
        >
          Cloud-first email, beautifully brewed
        </p>
      </div>

      <style>{`
        @keyframes authFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
